import { isIsoDateLike } from "./date-format";

type VizRow = Record<string, unknown>;

const DEFAULT_MAX_CATEGORY_LABEL_LENGTH = 18;
const DEFAULT_CATEGORY_LABEL_SAMPLE_SIZE = 12;
const DEFAULT_ROTATION_LENGTH_THRESHOLD = 10;

/**
 * カテゴリ軸ラベルを指定長で省略表示する。
 */
export function formatCategoryTickLabel(args: {
  label: string;
  maxLength?: number;
}): string {
  const maxLength = args.maxLength ?? DEFAULT_MAX_CATEGORY_LABEL_LENGTH;
  if (args.label.length <= maxLength) return args.label;
  return `${args.label.slice(0, maxLength - 1)}…`;
}

/**
 * チャート高さ props を安全に丸める。
 *
 * @remarks
 * 有限数値以外は `null` を返し、呼び出し側で既定高さへフォールバックする。
 */
export function resolveRequestedChartHeight(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.round(value);
}

export type CategoricalAxisLabelAnalysis = {
  sampleLabels: string[];
  shouldRotate: boolean;
};

/**
 * カテゴリ軸ラベルの回転要否とサンプルラベル群を解決する。
 *
 * @remarks
 * 日付らしい文字列か、一定文字数を超えるラベルが含まれる場合のみ回転を有効化する。
 */
export function analyzeCategoricalAxisLabels(args: {
  rows: VizRow[];
  categoryKey: string;
  formatCategoryAxisTick: (value: unknown) => string;
  isVertical: boolean;
  sampleSize?: number;
  rotateLengthThreshold?: number;
}): CategoricalAxisLabelAnalysis {
  const sampleRows = args.rows.slice(
    0,
    args.sampleSize ?? DEFAULT_CATEGORY_LABEL_SAMPLE_SIZE,
  );
  const sampleLabels = sampleRows.map((row) =>
    args.formatCategoryAxisTick(row[args.categoryKey]),
  );
  if (args.isVertical) {
    return {
      sampleLabels,
      shouldRotate: false,
    };
  }

  const rotateLengthThreshold =
    args.rotateLengthThreshold ?? DEFAULT_ROTATION_LENGTH_THRESHOLD;
  const shouldRotate = sampleRows.some((row, index) => {
    const rawLabel = String(row[args.categoryKey] ?? "");
    const formattedLabel = sampleLabels[index] ?? "";
    return (
      isIsoDateLike(rawLabel) || formattedLabel.length > rotateLengthThreshold
    );
  });

  return {
    sampleLabels,
    shouldRotate,
  };
}

/**
 * カテゴリ軸の表示幅を、サンプルの最大ラベル長から算出する。
 */
export function resolveCategoryAxisWidth(args: {
  rows: VizRow[];
  categoryKey: string;
  formatCategoryAxisTick: (value: unknown) => string;
  maxLabelLength?: number;
  minWidth?: number;
  maxWidth?: number;
  characterWidthPx?: number;
  paddingPx?: number;
}): number {
  const maxLabelLength =
    args.maxLabelLength ?? DEFAULT_MAX_CATEGORY_LABEL_LENGTH;
  const minWidth = args.minWidth ?? 96;
  const maxWidth = args.maxWidth ?? 220;
  const characterWidthPx = args.characterWidthPx ?? 7;
  const paddingPx = args.paddingPx ?? 24;

  const boundedMaxLabelLength = args.rows.reduce((maxLength, row) => {
    const label = args.formatCategoryAxisTick(row[args.categoryKey]);
    return Math.max(maxLength, Math.min(label.length, maxLabelLength));
  }, 0);

  const width = boundedMaxLabelLength * characterWidthPx + paddingPx;
  return Math.max(minWidth, Math.min(maxWidth, width));
}
